import { Vector3, type Camera } from 'three'
import { ButtonCooldownGate, GamePlayUiState } from './gamePlayUiState'
import { TutorialCanvas } from './tutorialCanvas'
import { AnimatedText, FAIL_TEXT } from './animatedText'
import { SceneWipe } from './sceneWipe'
import { FailPopup, SuccessPopup, type PopupHandlers } from './popups'
import { NoAdsPopup } from './noAdsPopup'
import { iapService } from '../monetization/iap'
import { SettingsPopup, type SettingsHandlers, type SettingsState } from './settingsPopup'

/**
 * The gameplay HUD — a port of `GamePlayUIPresenter`, not a lookalike.
 *
 * ## What the scene still gives us, and what it does not
 *
 * `Gameplay.unity`'s `Canvas` survived AssetRipper better than this file used
 * to claim. Two facts have to be kept apart:
 *
 *  - **Layout: 📐 intact.** All 40 `RectTransform` (u!224) keep
 *    `m_AnchorMin/Max`, `m_AnchoredPosition`, `m_SizeDelta`, `m_Pivot`,
 *    `m_LocalScale` and the parent/child links. Every number in `index.html` is
 *    copied from the scene, not eyeballed off a screenshot.
 *  - **Content: ❌ gone.** All 69 `MonoBehaviour` (u!114) are stripped to a
 *    `m_Script` guid — so `Image.m_Sprite`, `Image.m_Color`,
 *    `TextMeshProUGUI.m_text`, `m_fontAsset` and `Button.m_OnClick` are all
 *    zero matches across the whole `ExportedProject`.
 *
 * The pictures themselves are not gone, only the *references* to them. They are
 * matched back by name out of the shipped sprite set (📐): `btn_2_base` is the
 * gold ring, `btn_2_blue` the disc, `restarticon` / `Settings` the glyphs,
 * `TextBox1` the level pill.
 *
 * ## Where the rules live
 *
 * The presenter's decisions — which button is interactable when, and the
 * one-second click cooldown — are in `gamePlayUiState.ts`, with no DOM in
 * reach, so they can be tested against the decomp. This file is the view that
 * drives elements from that state.
 */
export interface GameUiHandlers extends PopupHandlers, SettingsHandlers {
  /**
   * `GamePlayUIModel.OnRestartButtonClicked` — 🔍
   * `GamePlayUIPresenter$$OnRestartButtonClicked__0x21471ac.c`.
   *
   * The presenter's own half (disabling the button) happens here; this is the
   * model's half: play `ButtonClick`, publish `LevelFailedEvent`, then load the
   * scene-transition popup. The port has no popup layer yet, so the host
   * reloads the level instead — see `main.ts`.
   */
  onRestart(): void
  /** `GamePlayUIModel.OnSettingsButtonClicked` — loads the settings popup. */
  onSettings(): void
  /**
   * ❓ PLACEHOLDER — no such button in the shipped HUD. Opens the Remove Ads
   * panel; see `web/docs/ADS-MONETIZATION.md`.
   */
  onNoAds(): void
  /** ❓ Not in the game — the Remove Ads panel's own close. */
  onCloseNoAds(): void
}

const _world = new Vector3()

/**
 * `ButtonFeedback` — 🔍 `ButtonFeedback$$*.c`, `dump.cs:712775`.
 *
 * Press: `if (_isActive && !_isPressed)` → `Taptic.Medium()`, `_isPressed = 1`,
 * invoke `OnPressed`, kill the running sequence, then
 * `transform.DOScale(pressScale, pressDuration).SetEase(...)`.
 * Release: `if (_isActive)` → invoke `OnReleased`, `_isPressed = 0`, kill, then
 * `transform.DOScale(1f, pressDuration)` — **no** `SetEase` call on this side.
 *
 * `pressScale` and `pressDuration` are `[SerializeField]`, so the scene's values
 * are stripped — but the class's own field initialisers survive in the
 * constructor (🔍 `ButtonFeedback$$.ctor__0x2137718.c`), which writes the eight
 * bytes `0x3e99999a3f733333` across `0x34`/`0x38`. Little-endian that is
 * `0x3f733333` = **0.95** into `pressScale` and `0x3e99999a` = **0.3** into
 * `pressDuration`. Those are the values unless the scene overrode them, and the
 * override is exactly what cannot be read.
 *
 * ❓ The ease of the press half went with `SetEase`'s argument — IL2CPP drops the
 * enum. The release half is DOTween's default (`OutQuad`) because the decomp
 * shows no `SetEase` there at all.
 *
 * ⬜ `Taptic.Medium()` on press is not ported: the port has no haptics layer,
 * and folding a vibration into the sound channel would be an invention.
 */
class ButtonFeedback {
  static readonly PRESS_SCALE = 0.95
  static readonly PRESS_DURATION = 0.3

  /** `_isActive` (0x48), seeded from `activateOnStart` (0x30, ctor default 1). */
  private active = true
  /** `_isPressed` (0x49). */
  private pressed = false

  constructor(private readonly element: HTMLElement) {
    element.style.transition = `transform ${ButtonFeedback.PRESS_DURATION}s ease-out`
  }

  /**
   * `Behaviour.enabled` on the component. `SetRestartButtonInteractable` drives
   * this in lockstep with `Selectable.interactable`, then calls `Release()`.
   */
  setEnabled(value: boolean): void {
    this.active = value
  }

  /** `OnPointerDown`. */
  press(): void {
    if (!this.active || this.pressed) return
    this.pressed = true
    this.element.style.transform = `scale(${ButtonFeedback.PRESS_SCALE})`
  }

  /** `OnPointerUp` / `Release` — the same body, minus the `_isActive` gate. */
  release(): void {
    this.pressed = false
    this.element.style.transform = 'scale(1)'
  }
}

export class GameUi {
  private readonly root: HTMLElement
  private readonly levelText: HTMLElement
  private readonly banner: HTMLElement
  private readonly restartButton: HTMLButtonElement
  private readonly settingsButton: HTMLButtonElement
  /** ❓ Not in the game — the Remove Ads entry point. */
  private readonly noAdsButton: HTMLButtonElement
  private readonly restartFeedback: ButtonFeedback
  private readonly settingsFeedback: ButtonFeedback
  private readonly cooldown = new ButtonCooldownGate()
  /** The presenter's rules, kept where a test can reach them without a DOM. */
  private readonly state = new GamePlayUiState()

  /** `TutorialCanvas`, the level-1 hand. Instantiated at runtime, as it is there. */
  private readonly tutorial: TutorialCanvas
  /** `AnimatedText` — the scene node the fail path switches on. */
  private readonly animated: AnimatedText
  /** `SceneLoadPresenter` — the iris the restart button loads. */
  private readonly wipe: SceneWipe
  /** `FailPresenter` / `SuccessPresenter`, the two panels that end a level. */
  private readonly failPopup: FailPopup
  private readonly successPopup: SuccessPopup
  /** `SettingsPresenter`, behind the gear. */
  private readonly settingsPopup: SettingsPopup
  /**
   * ❓ Not in the game — the Remove Ads panel.
   *
   * It lives here, not in `main.ts`, for one concrete reason: every rect in
   * its stylesheet is in `--u`, the CanvasScaler unit, and `--u` is declared
   * on `#ui`. Mounted anywhere else the variable does not resolve and the
   * whole panel collapses to nothing — measured, not guessed.
   */
  private readonly noAdsPopup: NoAdsPopup

  /** `ConveyorLimitText` — buses committed to the road against the cap. */
  private readonly capacity: HTMLElement
  private capacityAnchor: Vector3 | null = null

  constructor(handlers: GameUiHandlers) {
    this.root = must('ui')
    this.levelText = must('levelText')
    this.banner = must('banner')
    this.restartButton = must('btnRestart') as HTMLButtonElement
    this.settingsButton = must('btnSettings') as HTMLButtonElement
    this.noAdsButton = must('btnNoAds') as HTMLButtonElement

    this.restartFeedback = new ButtonFeedback(this.restartButton)
    this.settingsFeedback = new ButtonFeedback(this.settingsButton)

    // `Initialize` wires settings first, restart second — the order is visible
    // in the decomp and is kept, though nothing observable depends on it.
    this.onClick(this.settingsButton, 'settings', () => handlers.onSettings())
    // ❓ Not in the game. Same cooldown gate as the other two, so a double tap
    // cannot stack two panels.
    this.onClick(this.noAdsButton, 'noAds', () => handlers.onNoAds())
    this.onClick(this.restartButton, 'restart', () => {
      // 🔍 `OnRestartButtonClicked` opens with
      // `SetRestartButtonInteractable(false)` before it touches the model.
      this.state.restartClicked()
      this.apply()
      // The model loads `SceneLoadPresenter`, whose `Show()` closes the iris and
      // only *then* changes scene; the port reloads on the same callback.
      this.wipe.close(() => handlers.onRestart())
    })

    for (const [button, feedback] of [
      [this.restartButton, this.restartFeedback],
      [this.settingsButton, this.settingsFeedback],
    ] as const) {
      button.addEventListener('pointerdown', () => feedback.press())
      button.addEventListener('pointerup', () => feedback.release())
      button.addEventListener('pointercancel', () => feedback.release())
      button.addEventListener('pointerleave', () => feedback.release())
    }

    this.capacity = document.createElement('div')
    this.capacity.id = 'capacity'
    this.capacity.className = 'worldLabel'
    this.root.append(this.capacity)

    this.tutorial = new TutorialCanvas(this.root)
    this.animated = new AnimatedText(this.root)
    this.wipe = new SceneWipe(this.root)
    this.failPopup = new FailPopup(this.root, handlers)
    this.successPopup = new SuccessPopup(this.root, handlers)
    this.settingsPopup = new SettingsPopup(this.root, handlers)
    this.noAdsPopup = new NoAdsPopup(this.root, iapService, {
      onClose: () => handlers.onCloseNoAds(),
    })

    this.apply()
  }

  /**
   * `TutorialController.CheckAndShowTutorial` — the hand, on level 1 only.
   *
   * The gate in the decomp is `_levelService._userLevel != 1`, and the target is
   * `BusController._boxes[0]`; both live at the call site because this class has
   * no reach into the rules.
   */
  showTutorial(target: () => Vector3): void {
    this.tutorial.show(target)
  }

  /** `TutorialController.OnBusClicked` — the first tap ends it. */
  hideTutorial(): void {
    this.tutorial.hide()
  }

  /**
   * `GamePlayUIPresenter.LevelLoaded(int)` — 🔍 `__0x21472c8.c`:
   *
   *     levelText.text = string.Format(<literal>, level);
   *     SetRestartButtonInteractable(false);
   *     SetSettingsButtonInteractable(true);
   *
   * The format literal is one of the strings Ghidra drops (operand
   * `UNK_06fde468`), so it does not come from the decomp — it comes from the
   * clip: `reference/gameplay.mp4` reads “Level 8” on the pill at t = 2, 10 and
   * 25 s, which fixes the format at `Level {0}` (📐).
   *
   * ⚠️ The *number* is still not quite the original's. `GamePlayUIModel` invokes
   * this with `_levelService`'s field `0x10` — the player's running level count,
   * which keeps climbing past 40 — while the port passes the level file's own
   * number. The two agree until the loop wraps.
   */
  setLevel(number: number): void {
    this.levelText.textContent = `Level ${number}`
    this.cooldown.clear()
    this.state.levelLoaded()
    this.apply()
  }

  /**
   * `SceneLoadPresenter.Close()` — the iris opens again once the new scene is
   * standing. The caller decides when that is; it is deliberately *not* the end
   * of `setLevel`, because the rebuild carries on after it.
   */
  showBoard(): void {
    this.wipe.open()
  }

  /**
   * `GamePlayUIPresenter.OnFirstInputDetected` — 🔍 `__0x21474a8.c`.
   *
   * Published by `BusController.OnBoxClicked` (`__0x21066dc.c`:74-82) behind its
   * own one-shot flag (0x94), on the first tap that reaches a bus in `_boxes` —
   * *before* the ice and road-space branches, so a refused tap counts too.
   */
  firstInput(): void {
    this.state.firstInput()
    this.apply()
  }

  /** `GamePlayUIPresenter.OnAllBusesDispatched` — 🔍 `__0x21474c4.c`. */
  allBusesDispatched(): void {
    this.state.allBusesDispatched()
    this.apply()
  }

  /** `GamePlayUIPresenter.OnLevelFailed` — 🔍 `__0x214747c.c`. */
  levelFailed(): void {
    this.state.levelFailed()
    this.apply()
  }

  /**
   * `SetRestartButtonInteractable` / `SetSettingsButtonInteractable` — 🔍
   * `__0x21471dc.c` and `__0x2147384.c`, twins down to the same 0xcc bytes:
   *
   *     button.interactable = v;
   *     if (feedback != null) { feedback.enabled = v; feedback.Release(); }
   *
   * The `Release()` tail is why a button disabled mid-press does not stay
   * squashed. Both run from here so the DOM can never drift from the state.
   */
  private apply(): void {
    this.restartButton.disabled = !this.state.restartInteractable
    this.restartFeedback.setEnabled(this.state.restartInteractable)
    this.restartFeedback.release()

    this.settingsButton.disabled = !this.state.settingsInteractable
    this.settingsFeedback.setEnabled(this.state.settingsInteractable)
    this.settingsFeedback.release()
  }

  /**
   * `BasePresenter.AddButtonListener` — every click the presenter owns goes
   * through the one-second cooldown, on an unscaled clock.
   */
  private onClick(button: HTMLElement, key: string, action: () => void): void {
    button.addEventListener('click', () => {
      if (!this.cooldown.tryClick(key, performance.now() / 1000)) return
      action()
    })
  }

  /**
   * Where the road-capacity label hangs, in three-space, or null to hide it.
   *
   * `ConveyorLimitText` is a child of `ConveyorIndicator`, and **all 40** levels
   * on the live set (`web/public/data/levels/`) carry a
   * `ConveyorIndicatorPosition`. The old claim here — "31 of 40, the other nine
   * show no counter" — was measured against `bustraffic_kit/data/levels/`, the
   * blended set `CLAUDE.md` forbids: the nine files missing there are exactly
   * 1, 4, 5, 7, 14, 16, 17, 18 and 21, which is the `levels_o` overlay.
   */
  setCapacityAnchor(position: Vector3 | null): void {
    this.capacityAnchor = position ? position.clone() : null
    this.capacity.style.display = position ? 'block' : 'none'
  }

  /**
   * `ConveyorIndicator.UpdateIndicator(current, max, isSpaceMaxed)` — the text
   * half only. 🔍 `__0x211825c.c`:
   *
   *     limitText.text = (current >= max || !isSpaceMaxed)
   *         ? string.Format(<fmt>, current, max)
   *         : <a second literal>;
   *
   * The format is 📐 from the clip, which reads `0/5`, `5/5` and `4/5` — so
   * `{0}/{1}`, no spaces.
   *
   * ⚠️ **The text does not go red.** The clip settles that: at 5/5 the number is
   * still white and it is the two lamps on the sign and the road's kerb that
   * turn red (`StartAlarm`, ported in `conveyorIndicatorView.ts`). An earlier
   * pass here tinted the label instead, which put the alarm in the wrong place.
   *
   * ❓ The second literal — what the sign shows when the count still leaves room
   * but no parked bus fits by **length** — is one of the strings Ghidra drops.
   * The port keeps showing the number in that state rather than invent a word
   * for it; the state itself is not lost, it drives the alarm either way.
   */
  setCapacity(onRoad: number, cap: number, _spaceMaxed = false): void {
    this.capacity.textContent = `${onRoad}/${cap}`
  }

  /**
   * `GameplayController.OnLevelFailed` — switch `AnimatedText` on, `Initialize`
   * it with one of two strings, and `Play`.
   *
   * ❓ Both strings, *and* which one: the `FailReason` the port's single loss
   * corresponds to is dropped by Ghidra at both publishers. `outOfMoves` is what
   * "nobody left who can be carried" means, so that is what it shows.
   */
  showFailBanner(): void {
    this.animated.play(FAIL_TEXT.outOfMoves)
  }

  /**
   * ⬜ Scaffolding for the **win** only. A loss drives `AnimatedText` above,
   * which is the scene's own node; a win in the original goes straight to
   * `SuccessPresenter`, a popup this port does not have. Empty text clears it.
   */
  setBanner(text: string): void {
    this.banner.textContent = text
    this.banner.classList.toggle('show', text.length > 0)
  }

  /** `GameplayController.OnLevelLoaded` — the banner goes back off. */
  clearFailBanner(): void {
    this.animated.hide()
  }

  /**
   * `<OnLevelFailed>g__TryShowFailPopup` — 🔍 `__0x211e610.c`:
   *
   *     if (!textDone || !crashDone) return;
   *     PopupService.Load(...);        // -> FailPresenter
   *
   * Both flags have to be set, so the panel waits for whichever of the banner
   * and the crash animation finishes last. The caller owns that timing because
   * the crash lives in the rules' half of the port.
   */
  showFailPopup(levelNumber: number): void {
    this.failPopup.setLevel(levelNumber)
    this.failPopup.open()
  }

  /**
   * `GameplayController.HandleLevelComplete` — 🔍 `__0x211d6d0.c`. No delay and
   * no in-scene banner: `TimeScaleChangedEvent(1f)`, `ToggleInput(false)`, then
   * straight to `PopupService.Load`.
   */
  showSuccessPopup(completedLevel: number): void {
    this.successPopup.show(completedLevel)
  }

  /** `SettingsPresenter.Initialize` — opens with the two switches already set. */
  showSettings(state: SettingsState): void {
    this.settingsPopup.show(state)
  }

  /** Every panel goes with the level it belongs to. */
  /**
   * ❓ Not in the game — hide the Remove Ads button once the entitlement is
   * owned. The panel behind it is then unreachable, which is what DiceFlow
   * does too (docs §A.2.4).
   */
  /** ❓ Not in the game — open the Remove Ads panel on the given ladder step. */
  showNoAds(level: number): void {
    this.noAdsPopup.open(level)
  }

  setAdsRemoved(removed: boolean): void {
    this.noAdsButton.classList.toggle('owned', removed)
  }

  closePopups(): void {
    this.failPopup.close()
    this.successPopup.close()
    this.settingsPopup.close()
    this.noAdsPopup.close()
  }

  get popupOpen(): boolean {
    return (
      this.failPopup.visible ||
      this.successPopup.visible ||
      this.settingsPopup.visible ||
      this.noAdsPopup.visible
    )
  }

  /**
   * `PopupService.Load<SceneLoadPresenter>` — the iris, for the paths that go
   * through a panel button rather than the HUD's restart.
   */
  showWipe(onDone: () => void): void {
    this.wipe.close(onDone)
  }

  /**
   * Follow the 3D anchor.
   *
   * The label is world-space in the original (a `TextMeshPro`, not a
   * `TextMeshProUGUI`), so it has to track the camera rather than sit at a fixed
   * corner — project it every frame and place it in the stage's own pixels.
   */
  sync(camera: Camera, dt = 0): void {
    const rect = this.root.getBoundingClientRect()
    // `TutorialCanvas.Update` -> `FollowTarget`, on the same clock.
    this.tutorial.sync(camera, dt, rect)

    if (!this.capacityAnchor) return
    _world.copy(this.capacityAnchor).project(camera)
    this.capacity.style.left = `${((_world.x + 1) / 2) * rect.width}px`
    this.capacity.style.top = `${((1 - _world.y) / 2) * rect.height}px`
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id)
  if (!element) throw new Error(`missing #${id} in index.html`)
  return element
}
